import { PDFDocument, FilterOptions, SortOption, NewPDFMessage, ConnectionState } from './types.js';
import { ContainerSignalRClient } from './container-signalr.js';
import { formatFileSize, formatDate, debounce } from './utils.js';
import { router, Route } from './router.js';
import { apiFetch } from './api-client.js';
import { setLastPage } from './last-page-store.js';

export interface ContainerBrowserConfig {
  containerId: string;
  containerName: string;
  containerColor: string;
  apiBaseUrl: string;
  signalRHubUrl: string;
}

export class ContainerBrowser {
  private documents: PDFDocument[] = [];
  private filteredDocuments: PDFDocument[] = [];
  private currentPage = 1;
  private pageSize = 12;
  private signalRClient: ContainerSignalRClient;
  private config: ContainerBrowserConfig;
  private currentConnectionState: ConnectionState = 'disconnected';
  private isShowingNotification = false;
  private initializeTimeoutId: number | null = null;
  private notificationTimeoutId: number | null = null;
  private isDisposed = false;
  private isInitialized = false;
  
  // Container SAS token caching
  private containerSasToken: string | null = null;
  private baseUrl: string | null = null;
  private tokenExpiry: number | null = null;

  // UI Elements
  private searchInput!: HTMLInputElement;
  private clearSearchBtn!: HTMLButtonElement;
  private sortSelect!: HTMLSelectElement;
  private refreshBtn!: HTMLButtonElement;
  private pdfGrid!: HTMLElement;
  private loadingState!: HTMLElement;
  private emptyState!: HTMLElement;
  private pagination!: HTMLElement;
  private prevPageBtn!: HTMLButtonElement;
  private nextPageBtn!: HTMLButtonElement;
  private pageInfo!: HTMLElement;
  private statusIndicator!: HTMLElement;
  private statusText!: HTMLElement;

  // State
  private currentFilters: FilterOptions = {
    search: '',
    sort: 'date-desc',
    page: 1,
    pageSize: 12
  };

  constructor(config: ContainerBrowserConfig) {
    this.config = config;
    this.signalRClient = new ContainerSignalRClient({
      containerId: config.containerId,
      containerName: config.containerName,
      hubUrl: config.signalRHubUrl
    });
    
    // Initialize everything after a brief delay to ensure DOM is ready
    this.initializeTimeoutId = window.setTimeout(() => {
      if (this.isDisposed) {
        return;
      }
      this.initialize();
    }, 100);
  }

  private initialize(): void {
    if (this.isDisposed || this.isInitialized) {
      return;
    }

    this.isInitialized = true;
    this.initializeElements();
    this.restorePageFromQuery();
    this.setupEventListeners();
    this.initializeSignalR();
    this.loadPDFs();
  }

  private initializeElements(): void {
    // Get all required DOM elements with error checking
    const elements = {
      searchInput: 'searchInput',
      clearSearchBtn: 'clearSearch',
      sortSelect: 'sortSelect',
      refreshBtn: 'refreshBtn',
      pdfGrid: 'pdfGrid',
      loadingState: 'loadingState',
      emptyState: 'emptyState',
      pagination: 'pagination',
      prevPageBtn: 'prevPage',
      nextPageBtn: 'nextPage',
      pageInfo: 'pageInfo',
      statusIndicator: 'statusIndicator',
      statusText: 'statusText'
    };

    const missing: string[] = [];

    // Check each element exists before assigning
    for (const [property, id] of Object.entries(elements)) {
      const element = document.getElementById(id);
      if (element) {
        (this as any)[property] = element;
      } else {
        missing.push(id);
      }
    }

    if (missing.length > 0) {
      throw new Error(`Required DOM elements not found: ${missing.join(', ')}`);
    }
  }

  private setupEventListeners(): void {
    // Search functionality with debouncing
    this.searchInput.addEventListener('input', debounce(() => {
      this.currentFilters.search = this.searchInput.value.trim();
      this.currentFilters.page = 1;
      this.filterAndRender();
    }, 300));

    this.clearSearchBtn.addEventListener('click', () => {
      this.searchInput.value = '';
      this.currentFilters.search = '';
      this.currentFilters.page = 1;
      this.filterAndRender();
    });

    // Sort functionality
    this.sortSelect.addEventListener('change', () => {
      this.currentFilters.sort = this.sortSelect.value as SortOption;
      this.currentFilters.page = 1;
      this.filterAndRender();
    });

    // Refresh functionality
    this.refreshBtn.addEventListener('click', () => {
      this.refresh();
    });

    // Pagination
    this.prevPageBtn.addEventListener('click', () => {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.currentFilters.page = this.currentPage;
        this.render();
      }
    });

    this.nextPageBtn.addEventListener('click', () => {
      const totalPages = Math.ceil(this.filteredDocuments.length / this.pageSize);
      if (this.currentPage < totalPages) {
        this.currentPage++;
        this.currentFilters.page = this.currentPage;
        this.render();
      }
    });
  }

  private restorePageFromQuery(): void {
    const pageParam = router.getQueryParams().get('page');
    if (!pageParam) return;

    const page = Number.parseInt(pageParam, 10);
    if (!Number.isInteger(page) || page < 1) return;

    this.currentPage = page;
    this.currentFilters.page = page;
  }

  private replacePageQuery(): void {
    if (router.getCurrentRoute() !== `/${this.config.containerId}`) return;

    const query = this.currentPage > 1 ? `?page=${this.currentPage}` : '';
    const route = `/${this.config.containerId}${query}`;
    window.history.replaceState({ route }, '', route);
  }

  private async initializeSignalR(): Promise<void> {
    if (this.isDisposed) {
      return;
    }

    try {
      // Set up event listeners BEFORE connecting
      this.signalRClient.on('newPDF', (pdf: NewPDFMessage) => {
        if (this.isDisposed) {
          return;
        }
        this.handleNewPDF(pdf);
      });

      this.signalRClient.on('connectionChange', (state: ConnectionState) => {
        if (this.isDisposed) {
          return;
        }
        this.handleConnectionChange(state);
      });

      console.log('SignalR event listeners set up, connecting...');
      // Now connect - this will trigger the connectionChange event automatically
      await this.signalRClient.connect();

      if (this.isDisposed) {
        await this.signalRClient.disconnect();
        return;
      }

      console.log('SignalR connection completed');

    } catch (error) {
      if (this.isDisposed) {
        return;
      }
      console.error('Failed to initialize SignalR:', error);
      this.showNotification('Failed to connect to real-time updates', 'error', 5000);
    }
  }

  private handleConnectionChange(state: ConnectionState): void {
    if (this.isDisposed) return;
    if (!this.statusIndicator || !this.statusText) return;

    console.log(`Container browser updating display to: ${state}`);
    this.currentConnectionState = state;
    // Only update display if we're not currently showing a notification
    if (!this.isShowingNotification) {
      this.updateConnectionDisplay();
    }
  }

  private updateConnectionDisplay(): void {
    if (this.isDisposed) return;
    if (!this.statusIndicator || !this.statusText) return;

    // Clear any inline styles to let CSS classes control everything
    this.statusIndicator.style.animation = '';
    this.statusIndicator.style.background = '';
    this.statusIndicator.className = `status-indicator ${this.currentConnectionState}`;
    
    switch (this.currentConnectionState) {
      case 'connected':
        this.statusText.textContent = 'Connected';
        break;
      case 'connecting':
        this.statusText.textContent = 'Connecting...';
        break;
      case 'reconnecting':
        this.statusText.textContent = 'Reconnecting...';
        break;
      case 'disconnected':
        this.statusText.textContent = 'Disconnected';
        break;
    }
  }

  public showNotification(message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info', duration: number = 3000): void {
    if (this.isDisposed) return;
    if (!this.statusIndicator || !this.statusText) return;

    this.isShowingNotification = true;
    
    // Apply notification styling using classes
    this.statusIndicator.className = `status-indicator notification-${type}`;
    this.statusText.textContent = message;

    if (this.notificationTimeoutId !== null) {
      window.clearTimeout(this.notificationTimeoutId);
    }

    // Restore after duration
    this.notificationTimeoutId = window.setTimeout(() => {
      if (this.isDisposed) {
        return;
      }
      this.isShowingNotification = false;
      this.updateConnectionDisplay();
    }, duration);
  }


  private async handleNewPDF(pdf: NewPDFMessage): Promise<void> {
    if (this.isDisposed) return;
    // Only process PDFs for this specific container
    if (pdf.containerName !== this.config.containerId) {
      console.log(`Ignoring PDF for different container: ${pdf.containerName} (expected: ${this.config.containerId})`);
      return;
    }

    // Try to construct URL using cached container SAS token, fallback to Event Grid URL
    let pdfUrl = await this.constructBlobUrl(pdf.fileName) || pdf.url;

    // Convert NewPDFMessage to PDFDocument
    const newDocument: PDFDocument = {
      id: this.generateId(),
      name: pdf.fileName,
      url: pdfUrl,
      size: pdf.size,
      uploadTime: pdf.uploadTime,
      lastModified: pdf.uploadTime,
      contentType: 'application/pdf',
      containerName: pdf.containerName
    };

    // Add to documents array
    this.documents.unshift(newDocument);
    
    // Update filtered documents and re-render
    this.filterAndRender();
    
    // Show notification
    this.showNotification(`New PDF added: ${pdf.fileName}`, 'success', 3000);

    // Auto-open the PDF with SAS token URL
    try {
      await this.autoOpenNewPDF(pdf.fileName);
    } catch (error) {
      console.error('Failed to auto-open new PDF:', error);
    }
  }

  private async autoOpenNewPDF(fileName: string): Promise<void> {
    if (this.isDisposed) return;
    try {
      // Try to construct URL using cached container SAS token with automatic refresh
      let pdfUrl = await this.constructBlobUrl(fileName);
      
      if (!pdfUrl) {
        // Final fallback: Get the PDF with SAS token from the API
        console.log('Could not construct SAS URL, fetching individual PDF token:', fileName);
        const encodedFileName = encodeURIComponent(fileName);
        const response = await apiFetch(`${this.config.apiBaseUrl}/pdfs/${encodedFileName}?container=${this.config.containerId}`);
        
        if (!response.ok) {
          throw new Error(`Failed to get PDF with SAS token: ${response.status}`);
        }
        
        const pdfWithSasToken: PDFDocument = await response.json();
        pdfUrl = pdfWithSasToken.url;
      }
      
      // Create PDF document object
      const pdfDocument: PDFDocument = {
        id: fileName,
        name: fileName,
        url: pdfUrl,
        size: 0, // Size not available for new PDFs
        uploadTime: new Date().toISOString(),
        lastModified: new Date().toISOString(),
        contentType: 'application/pdf',
        containerName: this.config.containerName
      };
      
      // Update the document in our array with the SAS token URL
      const docIndex = this.documents.findIndex(doc => doc.name === fileName);
      if (docIndex !== -1) {
        this.documents[docIndex].url = pdfUrl;
      }
      
      // Auto-open the PDF
      await this.openPDF(pdfDocument);
      
    } catch (error) {
      console.error('Failed to fetch SAS token for new PDF:', error);
      this.showNotification(`New PDF added but couldn't auto-open: ${fileName}`, 'warning', 4000);
    }
  }

  private generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substring(2);
  }

  private isTokenValid(): boolean {
    return this.containerSasToken !== null && 
           this.baseUrl !== null && 
           this.tokenExpiry !== null && 
           Date.now() < this.tokenExpiry;
  }

  private async constructBlobUrl(filename: string, allowRefresh: boolean = true): Promise<string | null> {
    if (this.isDisposed) {
      return null;
    }

    if (!this.isTokenValid() && allowRefresh) {
      // Try to refresh the token once
      const refreshed = await this.refreshContainerSasToken();
      if (!refreshed) {
        return null;
      }
    }
    
    if (!this.isTokenValid()) {
      return null;
    }
    
    return `${this.baseUrl}/${encodeURIComponent(filename)}?${this.containerSasToken}`;
  }

  private async refreshContainerSasToken(): Promise<boolean> {
    if (this.isDisposed) {
      return false;
    }

    try {
      console.log('Refreshing container SAS token...');
      const response = await apiFetch(`${this.config.apiBaseUrl}/pdfs?container=${this.config.containerId}`);
      
      if (!response.ok) {
        throw new Error(`Failed to refresh SAS token: ${response.status}`);
      }
      
      const data = await response.json();

      if (this.isDisposed) {
        return false;
      }
      
      // Update cached container SAS token information
      if (data.containerSasToken && data.baseUrl) {
        this.containerSasToken = data.containerSasToken;
        this.baseUrl = data.baseUrl;
        // Set token expiry to 50 minutes (10 minutes before actual expiry for safety)
        this.tokenExpiry = Date.now() + (50 * 60 * 1000);
        console.log('Container SAS token refreshed successfully');
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Failed to refresh container SAS token:', error);
      return false;
    }
  }

  private async loadPDFs(): Promise<void> {
    if (this.isDisposed) {
      return;
    }

    this.setLoading(true);
    
    try {
      // Load PDFs from API for this specific container
      const response = await apiFetch(`${this.config.apiBaseUrl}/pdfs?container=${this.config.containerId}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();

      if (this.isDisposed) {
        return;
      }

      this.documents = data.documents || [];
      
      // Cache container SAS token information for constructing URLs for new PDFs
      if (data.containerSasToken && data.baseUrl) {
        this.containerSasToken = data.containerSasToken;
        this.baseUrl = data.baseUrl;
        // Set token expiry to 50 minutes (10 minutes before actual expiry for safety)
        this.tokenExpiry = Date.now() + (50 * 60 * 1000);
      }
      
      this.filterAndRender();
      
    } catch (error) {
      if (this.isDisposed) {
        return;
      }
      console.error('Failed to load PDFs:', error);
      this.showNotification('Failed to load PDFs. Please try again.', 'error', 5000);
      
      // Show empty state
      this.showEmptyState();
    } finally {
      if (this.isDisposed) {
        return;
      }
      this.setLoading(false);
    }
  }

  private filterAndRender(): void {
    this.filterDocuments();
    this.render();
  }

  private filterDocuments(): void {
    let filtered = [...this.documents];

    // Apply search filter
    if (this.currentFilters.search) {
      const searchTerm = this.currentFilters.search.toLowerCase();
      filtered = filtered.filter(doc => 
        doc.name.toLowerCase().includes(searchTerm) ||
        doc.metadata?.title?.toLowerCase().includes(searchTerm) ||
        doc.metadata?.author?.toLowerCase().includes(searchTerm)
      );
    }

    // Apply sort
    filtered.sort((a, b) => {
      switch (this.currentFilters.sort) {
        case 'date-desc':
          return new Date(b.uploadTime).getTime() - new Date(a.uploadTime).getTime();
        case 'date-asc':
          return new Date(a.uploadTime).getTime() - new Date(b.uploadTime).getTime();
        case 'name-asc':
          return a.name.localeCompare(b.name);
        case 'name-desc':
          return b.name.localeCompare(a.name);
        case 'size-desc':
          return b.size - a.size;
        case 'size-asc':
          return a.size - b.size;
        default:
          return 0;
      }
    });

    this.filteredDocuments = filtered;
  }

  private render(): void {
    if (this.filteredDocuments.length === 0) {
      this.showEmptyState();
      return;
    }

    const totalPages = Math.max(1, Math.ceil(this.filteredDocuments.length / this.pageSize));
    if (this.currentPage > totalPages) {
      this.currentPage = totalPages;
      this.currentFilters.page = totalPages;
    } else if (this.currentPage < 1) {
      this.currentPage = 1;
      this.currentFilters.page = 1;
    }

    this.replacePageQuery();

    this.renderPDFGrid();
    this.renderPagination();
  }

  private renderPDFGrid(): void {
    if (!this.pdfGrid) return;

    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    const pageDocuments = this.filteredDocuments.slice(startIndex, endIndex);

    this.pdfGrid.innerHTML = '';
    this.pdfGrid.style.display = 'grid';

    pageDocuments.forEach(doc => {
      const pdfCard = this.createPDFCard(doc);
      this.pdfGrid.appendChild(pdfCard);
    });
  }

  private createPDFCard(doc: PDFDocument): HTMLElement {
    const card = document.createElement('div');
    card.className = 'pdf-card';
    card.style.cssText = `
      background: white;
      border-radius: 12px;
      padding: 1.5rem;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      cursor: pointer;
      transition: all 0.3s ease;
      border-left: 4px solid ${this.config.containerColor};
    `;

    // Add hover effects
    card.addEventListener('mouseenter', () => {
      card.style.transform = 'translateY(-4px)';
      card.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.15)';
    });

    card.addEventListener('mouseleave', () => {
      card.style.transform = 'translateY(0)';
      card.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
    });

    // Add click handler
    card.addEventListener('click', async () => {
      await this.openPDF(doc);
    });

    // Card content
    card.innerHTML = `
      <div class="pdf-icon" style="font-size: 2rem; margin-bottom: 1rem;">📄</div>
      <h3 class="pdf-name" style="font-size: 1.1rem; font-weight: 600; margin-bottom: 0.5rem; color: #2c3e50;">${doc.name}</h3>
      <p class="pdf-size" style="font-size: 0.9rem; color: #7f8c8d; margin-bottom: 0.5rem;">${formatFileSize(doc.size)}</p>
      <p class="pdf-date" style="font-size: 0.9rem; color: #95a5a6;">${formatDate(doc.uploadTime)}</p>
    `;

    return card;
  }

  private renderPagination(): void {
    if (!this.pagination) return;

    const totalPages = Math.ceil(this.filteredDocuments.length / this.pageSize);
    
    if (totalPages <= 1) {
      this.pagination.style.display = 'none';
      return;
    }

    this.pagination.style.display = 'flex';
    this.prevPageBtn.disabled = this.currentPage <= 1;
    this.nextPageBtn.disabled = this.currentPage >= totalPages;
    this.pageInfo.textContent = `Page ${this.currentPage} of ${totalPages}`;
  }

  private showEmptyState(): void {
    if (this.pdfGrid) this.pdfGrid.style.display = 'none';
    if (this.pagination) this.pagination.style.display = 'none';
    if (this.emptyState) this.emptyState.style.display = 'block';
  }

  private setLoading(loading: boolean): void {
    if (this.isDisposed) return;
    if (this.loadingState) {
      this.loadingState.style.display = loading ? 'block' : 'none';
    }
    
    if (this.pdfGrid) {
      this.pdfGrid.style.display = loading ? 'none' : 'grid';
    }
  }

  private async openPDF(doc: PDFDocument): Promise<void> {
    if (this.isDisposed) return;
    setLastPage(this.config.containerId, doc.name, this.currentPage);
    // Navigate to the PDF viewer page with the filename as a URL parameter
    const viewerRoute = `/${this.config.containerId}/view?file=${encodeURIComponent(doc.name)}` as Route;
    router.navigate(viewerRoute);
  }

  /**
   * Fetch a PDF document by name with a fresh SAS token
   * This is useful for loading PDFs directly via URL without having the full document list
   */
  public async fetchPDFByName(fileName: string): Promise<PDFDocument> {
    try {
      // First, check if we have it in our cached documents
      const cachedDoc = this.documents.find(doc => doc.name === fileName);

      // Try to construct URL using cached container SAS token
      let pdfUrl = await this.constructBlobUrl(fileName);

      if (!pdfUrl) {
        // Fallback: Get the PDF with a fresh SAS token from the API
        console.log('Fetching PDF with fresh SAS token from API:', fileName);
        const encodedFileName = encodeURIComponent(fileName);
        const response = await apiFetch(`${this.config.apiBaseUrl}/pdfs/${encodedFileName}?container=${this.config.containerId}`);

        if (!response.ok) {
          throw new Error(`Failed to fetch PDF: ${response.status} ${response.statusText}`);
        }

        const pdfWithSasToken: PDFDocument = await response.json();
        return pdfWithSasToken;
      }

      // If we have cached metadata, use it with the fresh URL
      if (cachedDoc) {
        return {
          ...cachedDoc,
          url: pdfUrl
        };
      }

      // Otherwise, create a basic document with the fresh URL
      return {
        id: fileName,
        name: fileName,
        url: pdfUrl,
        size: 0,
        uploadTime: new Date().toISOString(),
        lastModified: new Date().toISOString(),
        contentType: 'application/pdf',
        containerName: this.config.containerName
      };

    } catch (error) {
      console.error('Failed to fetch PDF by name:', error);
      throw error;
    }
  }

  public refresh(): void {
    if (this.isDisposed) return;
    this.loadPDFs();
  }

  public getCurrentFilters(): FilterOptions {
    return { ...this.currentFilters };
  }

  public destroy(): void {
    this.isDisposed = true;

    if (this.initializeTimeoutId !== null) {
      window.clearTimeout(this.initializeTimeoutId);
      this.initializeTimeoutId = null;
    }

    if (this.notificationTimeoutId !== null) {
      window.clearTimeout(this.notificationTimeoutId);
      this.notificationTimeoutId = null;
    }

    // Clean up SignalR connection
    this.signalRClient.disconnect();
  }
}
